import { CATEGORY_PUBLICITY } from '../../domain/domainCodes.js'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CircleHelp,
  Clock,
  FileArchive,
  FileSpreadsheet,
  Film,
  Layers,
  Pencil,
  Plus,
  Trash2,
  Video,
} from 'lucide-react'
import Button from '../../components/Button'
import { useRemembered } from '../../components/listMemory'
import ChapterFilesDrawer from './ChapterFilesDrawer'
import ConfirmDialog from '../../components/ConfirmDialog'
import ExcelImportModal from '../../components/ExcelImportModal'
import { excel as EXCEL } from '../../components/strings'
import DataTable from '../../components/DataTable'
import FilterBar from '../../components/FilterBar'
import PageHeader from '../../components/PageHeader'
import RowActions from '../../components/RowActions'
import StatStrip from '../../components/StatStrip'
import Select from '../../components/Select'
import StatusBadge from '../../components/StatusBadge'
import { useToast } from '../../components/toastContext'
import {
  deleteCategory,
  exportCategory,
  exportCategoryQuestions,
  importCategory,
  listCategories,
} from './contentService'
import { categoriesStrings as S } from './strings'
import './content.css'
import { serverText } from '../../lib/i18n'

/** CategoryPublicity — Public = 0, Private = 1. */
const PUBLICITY_OPTIONS = [
  { value: String(CATEGORY_PUBLICITY.PUBLIC), label: S.filter.public },
  { value: String(CATEGORY_PUBLICITY.PRIVATE), label: S.filter.private },
]

/**
 * إدارة المواضيع — Views/CategoriesManagement/Index.cshtml.
 *
 * Three of the legacy headers are icon-only with the wording in a `title`; the same
 * pairing is kept here, with the text as the accessible name.
 */
export default function CategoriesPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const [publicity, setPublicity] = useRemembered('categories:publicity', '')
  const [reloadKey, setReloadKey] = useState(0)
  const [query, setQuery] = useRemembered('categories:query', '')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  /* A FAILED load is not an empty one — without this the table renders its "no
     records" state for a server error, which is the opposite of the truth. */
  const [loadError, setLoadError] = useState(null)
  const [pendingDelete, setPendingDelete] = useState(null)
  /* The topic whose chapter videos are open in the player. */
  const [viewingFiles, setViewingFiles] = useState(null)
  const [importing, setImporting] = useState(false)

  /**
   * The whole list, not a page of it.
   *
   * The endpoint pages, and the summary above the table sums chapters and questions ACROSS
   * TOPICS — off one page those figures would be wrong. Topics are a bounded set (the
   * server's own clamp is 100 and a library runs to tens), so the list is read whole and
   * the table pages it locally. Programs and courses, which are not bounded, do not get
   * this treatment.
   */
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listCategories({
      publicity: publicity === '' ? null : Number(publicity),
      pageSize: 100,
    })
      .then((response) => {
        if (cancelled) return
        setLoadError(null)
        setRows(response.rows)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setLoadError(error)
        setRows([])
        setLoading(false)
        toast.error(error?.message || S.deleteFailed)
      })
    return () => {
      cancelled = true
    }
  }, [publicity, reloadKey, toast])

  /* Totalled off the rows already on the page — the same four figures the table
     shows per row, summed. Read-only: there is nothing here to narrow a topic
     list down to. */
  /* Both exports answer EITHER a file or a refusal as JSON, and `download.js` turns the
     second into an error rather than saving a file whose contents are the message. */
  const runExport = async (pending) => {
    const outcome = await pending
    if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
    toast.success(S.exported(outcome.filename))
  }

  const stats = useMemo(() => {
    // The two length fields arrive as strings ("243"), so a bare `+` would
    // concatenate them into one very long number instead of adding them.
    const sum = (key) => rows.reduce((total, row) => total + (Number(row[key]) || 0), 0)
    return {
      total: rows.length,
      chapters: sum('chapters'),
      questions: sum('questions'),
      minutes: sum('arabicLength') + sum('englishLength'),
    }
  }, [rows])

  const columns = useMemo(
    () => [
      {
        key: 'index',
        header: S.columns.index,
        width: '64px',
        sortable: false,
        searchable: false,
        render: (_row, index) => <span className="content__index">{index + 1}</span>,
      },
      { key: 'name', header: S.columns.name, render: (row) => <bdi>{row.name}</bdi> },
      {
        key: 'arabicLength',
        header: <Clock size={15} strokeWidth={1.75} aria-label={S.columns.arabicLength} />,
        align: 'center',
        width: '76px',
      },
      {
        key: 'nameEn',
        header: S.columns.nameEn,
        render: (row) => <bdi dir="ltr">{row.nameEn}</bdi>,
      },
      {
        key: 'englishLength',
        header: <Clock size={15} strokeWidth={1.75} aria-label={S.columns.englishLength} />,
        align: 'center',
        width: '76px',
      },
      {
        key: 'chapters',
        header: <Video size={15} strokeWidth={1.75} aria-label={S.columns.chapters} />,
        align: 'center',
        width: '76px',
      },
      { key: 'questions', header: S.columns.questions, align: 'center' },
      {
        key: 'createdOn',
        header: S.columns.createdOn,
        render: (row) => (
          <span className="content__date" dir="ltr">
            {row.createdOn ?? ''}
          </span>
        ),
      },
      {
        key: 'publicity',
        header: S.columns.publicity,
        value: (row) => (row.publicity === CATEGORY_PUBLICITY.PUBLIC ? S.filter.public : S.filter.private),
        render: (row) => (
          <StatusBadge tone={row.publicity === CATEGORY_PUBLICITY.PUBLIC ? 'success' : 'dark'}>
            {row.publicity === CATEGORY_PUBLICITY.PUBLIC ? S.filter.public : S.filter.private}
          </StatusBadge>
        ),
      },
      {
        key: 'actions',
        header: <Pencil size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '96px',
        sortable: false,
        searchable: false,
        render: (row) => (
          /* Four actions: the two edits inline, the two EXPORTS folded away. Both had
             been written in the service with nothing in front of them. */
          <RowActions
            actions={[
              {
                id: 'edit',
                icon: Pencil,
                label: S.row.edit,
                onClick: () => navigate(`/categories/${row.id}/edit`),
              },
              /* الملفات المرئية — the legacy's green video button, which opens the topic's
                 chapters as a playlist and plays them. It reads what is on disk; nothing
                 here uploads or deletes. */
              {
                id: 'chapterFiles',
                icon: Film,
                label: S.row.chapterFiles,
                tone: 'primary',
                onClick: () => setViewingFiles(row),
              },
              {
                id: 'delete',
                icon: Trash2,
                label: S.row.delete,
                danger: true,
                onClick: () => setPendingDelete(row),
              },
              {
                id: 'export',
                icon: FileArchive,
                label: S.row.exportTopic,
                tone: 'primary',
                onClick: () => runExport(exportCategory(row.id)),
              },
              {
                id: 'export-questions',
                icon: FileSpreadsheet,
                label: S.row.exportQuestions,
                tone: 'primary',
                onClick: () => runExport(exportCategoryQuestions(row.id)),
              },
            ]}
          />
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate],
  )

  return (
    <>
      <PageHeader
        title={S.title}
        subtitle={S.subtitle}
        count={loading ? undefined : rows.length}
        actions={
          <>
            <Button onClick={() => navigate('/categories/new')}>
              <Plus size={16} strokeWidth={2} />
              {S.actions.create}
            </Button>
            <Button variant="soft" onClick={() => setImporting(true)}>
              <FileArchive size={16} strokeWidth={1.75} />
              {S.actions.upload}
            </Button>
          </>
        }
      />

      <StatStrip
        label={S.stats.caption}
        items={[
          { id: 'total', icon: Layers, label: S.stats.total, value: stats.total },
          {
            id: 'chapters',
            icon: Film,
            label: S.stats.chapters,
            value: stats.chapters,
            tone: 'info',
          },
          {
            id: 'questions',
            icon: CircleHelp,
            label: S.stats.questions,
            value: stats.questions,
            tone: 'gold',
          },
          {
            id: 'minutes',
            icon: Clock,
            label: S.stats.minutes,
            value: stats.minutes,
            tone: 'neutral',
          },
        ]}
      />

      <FilterBar
        search={{ value: query, onChange: setQuery, placeholder: S.filter.all }}
        chips={
          publicity === ''
            ? []
            : [
                {
                  id: 'publicity',
                  label: S.filter.publicity,
                  value: PUBLICITY_OPTIONS.find((option) => option.value === publicity)?.label ?? '',
                  onRemove: () => setPublicity(''),
                },
              ]
        }
      >
        <Select
          value={publicity}
          onChange={setPublicity}
          options={PUBLICITY_OPTIONS}
          placeholder={S.filter.publicity}
          aria-label={S.filter.publicity}
        />
      </FilterBar>

      <DataTable
        stateKey="categories"
        error={loadError}
        columns={columns}
        rows={rows}
        loading={loading}
        query={query}
        controls={false}
        getRowId={(row) => row.id}
        initialSort={{ key: 'name', dir: 'asc' }}
      />

      {/* NOT an excel import, despite the component's name: this takes a ZIP of a whole
          topic — its chapters, videos and question bank — exported from another
          installation. `CategoryFileUploadHandler` rejects anything not ending `.zip`, so
          the picker offers only that, and there is no template to download because there
          is no template: the file comes from `تصدير` on another server. */}
      <ExcelImportModal
        open={importing}
        title={S.actions.upload}
        accept=".zip"
        /* The shared wording is written for a spreadsheet: the hint says "Excel or CSV"
           and the notes say to download a template and keep the column order. Neither
           applies — this takes a ZIP and there is no template to download. */
        strings={{ intro: EXCEL.introZip, notes: EXCEL.notesZip }}
        onClose={() => setImporting(false)}
        /* The modal hands over the FILE itself, not `{ file }` — destructuring it here
           yielded undefined, `importCategory` returned its empty-file guard, and the sheet
           reported the generic failure without ever reaching the server. */
        onSubmit={async (file) => {
          const outcome = await importCategory({ file })
          if (!outcome.ok) return toast.error(outcome.message || S.deleteFailed)
          setImporting(false)
          toast.success(serverText(outcome.message) || S.imported)
          setReloadKey((key) => key + 1)
        }}
      />


      <ChapterFilesDrawer
        open={!!viewingFiles}
        category={viewingFiles}
        onClose={() => setViewingFiles(null)}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        message={S.deleteConfirm}
        cancelLabel={S.close}
        /* This used to close the dialog and toast "deleted" without calling anything —
           the topic, its chapters and its questions were all still there on reload. */
        onConfirm={async () => {
          const outcome = await deleteCategory(pendingDelete.id)
          if (!outcome.ok) return toast.error(outcome.message || S.deleteFailed)
          setPendingDelete(null)
          toast.success(serverText(outcome.message) || S.deleted)
          setReloadKey((key) => key + 1)
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  )
}
